import { Gamepad2, Palette, Users, TrendingUp, ArrowRight, Sparkles } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type IndustryImage = Tables<'industry_images'>;

const GamingPage = () => {
  const [industryImages, setIndustryImages] = useState<IndustryImage[]>([]);

  useEffect(() => {
    const fetchIndustryImages = async () => {
      const { data, error } = await supabase
        .from('industry_images')
        .select('*')
        .eq('industry', 'gaming')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching industry images:', error);
      } else {
        setIndustryImages(data || []);
      }
    };

    fetchIndustryImages();
  }, []);

  const industry = {
    title: "Gaming & Entertainment",
    icon: Gamepad2,
    gradient: "from-violet-500 to-purple-500",
    description: "Create character designs, concept art, and game assets that bring virtual worlds to life with unlimited creative possibilities.",
    stats: "10x asset production"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Palette className="w-8 h-8 text-violet-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Concept Art</h3>
          <p className="text-gray-300 text-sm">Visualize characters and environments</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-8 h-8 text-violet-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Game Assets</h3>
          <p className="text-gray-300 text-sm">Generate textures, items, and props</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-violet-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Rapid Prototyping</h3>
          <p className="text-gray-300 text-sm">Fast iteration on visual concepts</p>
        </CardContent>
      </Card>
    </div>
  );

  const imageGallery = (
    <div>
      <h3 className="text-2xl font-bold text-white text-center mb-8">AI-Generated Gaming Art Examples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {industryImages.length > 0 ? (
          industryImages.map((image) => (
            <Card key={image.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
              <div className="aspect-[4/3] relative overflow-hidden">
                <img 
                  src={image.image_url} 
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-300">{image.title}</p>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-3 text-center text-gray-400 py-12">
            <Gamepad2 className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">No gaming examples available yet</p>
            <p className="text-sm text-gray-400 mt-2">Images will be added soon</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      seo={{
        title: "AI Game Art Generator | Concept Art & Assets",
        description: "Create stunning game art and assets with AI. Generate concept art, character designs, environments, and game assets. Perfect for indie developers and studios.",
        keywords: "game art ai, concept art generator, gaming assets, character design ai, game development art, indie game graphics",
        url: "/industries/gaming"
      }}
      heroContent={
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6 text-center">
              <Palette className="w-8 h-8 text-violet-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Concept Art</h3>
              <p className="text-gray-300 text-sm">Visualize characters and environments</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-8 h-8 text-violet-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Game Assets</h3>
              <p className="text-gray-300 text-sm">Generate textures, items, and props</p>
            </CardContent>
          </Card>
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-8 h-8 text-violet-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Rapid Prototyping</h3>
              <p className="text-gray-300 text-sm">Fast iteration on visual concepts</p>
            </CardContent>
          </Card>
        </div>
      }
      mainContent={
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-6">Revolutionizing Game Development</h2>
          
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              The gaming industry demands constant visual innovation and rapid asset creation. AI-generated imagery is transforming how games are developed, from initial concept art to final in-game assets, enabling smaller teams to create AAA-quality visuals.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The Game Development Challenge</h3>
            <p>
              Modern games require thousands of visual assets - characters, environments, items, textures, and UI elements. Traditional art production is time-consuming and expensive, often requiring large teams and lengthy development cycles.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI-Powered Game Creation</h3>
            <ul className="list-disc list-inside space-y-3 text-gray-300">
              <li><strong className="text-white">Character Design:</strong> Generate unique characters, creatures, and NPCs</li>
              <li><strong className="text-white">Environment Art:</strong> Create landscapes, buildings, and atmospheric scenes</li>
              <li><strong className="text-white">Item Generation:</strong> Design weapons, armor, and collectibles at scale</li>
              <li><strong className="text-white">Texture Creation:</strong> Generate seamless textures and materials</li>
              <li><strong className="text-white">Concept Exploration:</strong> Rapidly prototype visual ideas and styles</li>
            </ul>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Industry Impact</h3>
            <p>
              Game studios using AI for asset generation report 10x increase in asset production speed, 70% reduction in concept art costs, and the ability to explore creative directions previously impossible due to time and budget constraints.
            </p>
            
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-6 mt-8">
              <h4 className="text-xl font-semibold text-violet-400 mb-3 flex items-center">
                <Gamepad2 className="w-5 h-5 mr-2" />
                Level Up Your Game Development
              </h4>
              <p className="text-gray-300">
                Create stunning game art and assets that bring your virtual worlds to life, faster and more affordably than ever before.
              </p>
            </div>
          </div>
        </div>
      }
      imageGallery={imageGallery}
    />
  );
};

export default GamingPage;
